import { isCustomerMessage, isSystemMessage } from '@microsoft/omnichannel-chat-sdk';
import { Activity, Message } from 'botframework-directlinejs';
import { LiveAgentState } from '@bingads-webui-react/msa-copilot-common';
import { getIsFromUser } from '@bingads-webui/ads-copilot-ui';

const MAX_MESSAGE_HISTORY_SIZE = 20 * 1000; // 20 KB

interface LiveAgentDLMessage {
  channelId?: string;
  conversationId?: string;
  fromId?: string;
  fromName?: string;
  id?: string;
  text?: string;
  value?: string | object;
}

export interface LiveAgentChatConfig {
  isAADUser?: boolean;
  surveyContext?: {
    firstName?: string;
    lastName?: string;
    localeId?: string;
    localeName?: string;
  },
  lwicontexts?: object;
}

interface CopilotMessage extends Message {
  value?: {
    QuestionId?: string;
    Sources?: Array<{
      Title?: string;
      Url?: string;
    }>;
    SiteLinks?: Array<{
      DisplayName?: string;
      Url?: string;
      SimilarityScore?: string;
    }>;
    StreamingStatus?: string;
    Intent?: string;
    Features?: unknown[];
    QuestionSuggestions?: Array<{
      Text?: string;
    }>;
  }
}

export const getLiveAgentDirectLineMessage = (message: LiveAgentDLMessage): Message => ({
  type: 'message',
  channelId: message.channelId,
  value: message.value as any,
  id: message.id,
  from: {
    id: message.fromId || '',
    name: message.fromName,
  },
  conversation: {
    id: message.conversationId,
  },
  text: message.text,
});

export const createStructuredHistory = (messages: Activity[], username: string, copilotConversationId?: string, maxMessageHistorySize: number = MAX_MESSAGE_HISTORY_SIZE): string => {
  const structuredHistory = messages
    .filter((message) => {
      const messageText = (message as Message)?.text;
      return messageText && messageText !== 'StaticMessages';
    })
    .map((message) => {
      const messageText = (message as Message)?.text;
      const userMessage = `**User: ${messageText}**`;
      const learnMoreLinks = (message as CopilotMessage)?.value?.Sources?.map(({ Url }) => Url);
      const learnMoreLinksMsg = learnMoreLinks?.length ? `<br><br>_Learn More links:_<br>${learnMoreLinks.join('<br>')}` : '';
      const siteLinks = (message as CopilotMessage)?.value?.SiteLinks?.map(({ Url }) => Url);
      const siteLinksMsg = siteLinks?.length ? `<br><br>_Site links:_<br>${siteLinks.join('<br>')}` : '';
      const copilotMessage = `Copilot: ${messageText}${learnMoreLinksMsg}${siteLinksMsg}`;
      const isFromUser = getIsFromUser(message, username);
      return isFromUser ? userMessage : copilotMessage;
    });

  // Add diagnosis information from troubleshootingSteps
  messages.forEach((message) => {
    const troubleshootingValue = (message as any)?.value?.troubleshootingValue;
    if (troubleshootingValue) {
      const { campaignId, campaignName, troubleshootingSteps } = troubleshootingValue;
      structuredHistory.push(`Diagnosis results for CampaignName: ${campaignName} and CampaignID: ${campaignId}`);
      troubleshootingSteps.forEach((step: any) => {
        step.diagnosis.forEach((diag: any) => {
          structuredHistory.push(`* Step: **${diag.stepName}**<br>**Has Issue:** ${diag.hasIssue}<br>Issue: ${diag.issue}`);
        });
      });
    }
  });

  let combinedSize = structuredHistory.reduce((totalSize, str) => totalSize + str.length, 0);
  if (combinedSize > maxMessageHistorySize) {
    for (let i = structuredHistory.length - 1; i >= 0; i -= 1) {
      if (combinedSize < maxMessageHistorySize) {
        break;
      } else if (structuredHistory[i].includes('Copilot') && structuredHistory[i].length > 500) {
        combinedSize = (combinedSize - structuredHistory[i].length) + 500;
        structuredHistory[i] = `${structuredHistory[i].slice(0, 500)}...`;
      }
    }
  }

  structuredHistory.splice(0, 0, 'User-Copilot Conversation History:');

  if (copilotConversationId) {
    structuredHistory.splice(1, 0, `**CopilotConversationId: '${copilotConversationId}'**`);
  }

  return structuredHistory
    .filter(str => str !== '')
    .join('<br></br>');
};

export const getLiveAgentFormattedMessage = (username: string, message: any, conversationId: string, isHiddenFromUser?: boolean) => {
  let fromName = '';
  let channelId = 'directline';
  let value: string | object = 'LiveAgentMessage';
  if (isCustomerMessage(message)) {
    fromName = username;
    channelId = 'user';
    value = '';

    if (isHiddenFromUser) {
      value = { isHiddenFromUser };
    }
  } else if (isSystemMessage(message)) {
    fromName = 'System';
  } else {
    fromName = message.sender.displayName;
  }

  let messageContent = message.content;
  if (message.tags && message.tags.includes(LiveAgentState.AGENT_ASSIGNMENT_READY)) {
    messageContent = LiveAgentState.AGENT_ASSIGNMENT_READY;
  } else if (message.tags && message.tags.includes(LiveAgentState.AGENT_ACCEPTED)) {
    messageContent = LiveAgentState.AGENT_ACCEPTED;
  }

  return getLiveAgentDirectLineMessage({
    id: message.id,
    text: messageContent,
    fromName,
    fromId: message.sender.id,
    conversationId,
    channelId,
    value,
  });
};

export const isCopilotChatHistoryMessage = (message: string) => message.includes('User-Copilot Conversation History');

// eslint-disable-next-line no-shadow
export enum LiveAgentConnectionState {
  DISCONNECTED,
  IN_PROGRESS,
  CONNECTED,
  ERROR,
}

export type LiveAgentTransferType = 'ConnectToAgentWithAIResponse'
  | 'ConnectToAgentNoAIResponse'
  | 'ConnectToAgentNoAIResponseRedScore'
  | 'ConnectToAgentWithAIResponseYellowScore';

// Keep the type for type checking
export type LiveAgentRedBucketReason = keyof typeof LiveAgentRedBucketReasonEnum;

// Add enum for runtime use
export const LiveAgentRedBucketReasonEnum = {
  // Red bucket reasons details
  directrequestsforsupport: 'directrequestsforsupport',
  frustrationordissatisfaction: 'frustrationordissatisfaction',
  urgenthelpneeded: 'urgenthelpneeded',
  accountterminationrequests: 'accountterminationrequests',
  legalorcomplianceissues: 'legalorcomplianceissues',
  capabilitylimitationsormissingdocumentation: 'capabilitylimitationsormissingdocumentation',
  repeatedqueries: 'repeatedqueries',
  unsatisfactoryresponsesfromotherfunctions: 'unsatisfactoryresponsesfromotherfunctions',
  // Yellow bucket reasons details
  copilotpartialanswer: 'copilotpartialanswer',
  pluginresponsenodataormetricsnotfound: 'pluginresponsenodataormetricsnotfound',
};

export const LiveAgentTransferMessages: Record<LiveAgentTransferType | LiveAgentRedBucketReason, string> = {
  ConnectToAgentWithAIResponse: _TL_('Still need assistance? Choose an option below to start a live chat.'),
  ConnectToAgentNoAIResponse: _TL_('A Microsoft Advertising Expert can help. For assistance, choose an option below to start a live chat.'),
  ConnectToAgentNoAIResponseRedScore: _TL_('Please contact a Microsoft Advertising Expert to help address your question. For assistance, choose an option below to start a live chat.'),
  ConnectToAgentWithAIResponseYellowScore: _TL_("If your request wasn't fully addressed, a Microsoft Advertising Expert can offer more help. For assistance, choose an option below to start a live chat."),
  // Red bucket reasons details
  directrequestsforsupport: _TL_('No problem. Choose a topic below to connect with a Microsoft Advertising Expert.'),
  frustrationordissatisfaction: _TL_('I understand this has been frustrating. Based on your request, it\'s best to connect with a Microsoft Advertising Expert. Choose a topic below to start a live chat.'),
  urgenthelpneeded: _TL_('I understand this is urgent. Please select a topic below so I can get you to the right Microsoft Advertising expert as quickly as possible.'),
  accountterminationrequests: _TL_('Got it. To close your account, choose a topic below to connect with a Microsoft Advertising Expert.'),
  legalorcomplianceissues: _TL_('To discuss this, choose a topic below to connect with a Microsoft Advertising Expert.'),
  capabilitylimitationsormissingdocumentation: _TL_('This is outside of Copilot\'s current capabilities. The team\'s working to expand what Copilot can do. In the meantime, choose a topic below to connect with a Microsoft Advertising Expert.'),
  repeatedqueries: _TL_('Sorry Copilot couldn\'t help this time. Choose a topic below to connect with a Microsoft Advertising Expert.'),
  unsatisfactoryresponsesfromotherfunctions: _TL_('Sorry, Copilot can\'t resolve this issue. Choose a topic below to connect with a Microsoft Advertising Expert.'),
  // Yellow bucket reasons details
  copilotpartialanswer: _TL_('If Copilot didn\'t fully answer your question, a Microsoft Advertising Expert can help. Choose a topic below to start a live chat.'),
  pluginresponsenodataormetricsnotfound: _TL_('If the response didn\'t solve your issue, choose a topic below to connect with a Microsoft Advertising Expert.'),
};
